import { useEffect, useRef, useState, useCallback } from 'react';
import { ExportJob } from '../services/jobs-api';
import { getKeycloak } from '../services/keycloak';

interface WSMessage {
  type: 'connected' | 'initial-state' | 'job-update' | 'pong';
  job?: ExportJob;
  jobs?: ExportJob[];
}

/**
 * Hook to manage real-time job updates via WebSocket
 * (Replaced SSE which doesn't support Authorization headers)
 */
export function useJobsSSE() {
  const [jobs, setJobs] = useState<Map<string, ExportJob>>(new Map());
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const pingIntervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    let isMounted = true;

    const connect = async () => {
      try {
        // Get Keycloak token
        const kc = getKeycloak();
        if (!kc || !kc.token) {
          console.log('⚠️ No Keycloak token available, cannot connect WebSocket');
          setConnected(false);
          return;
        }

        // Build WebSocket URL
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        const wsUrl = `${protocol}//${host}/api/export-jobs/ws?token=${encodeURIComponent(kc.token)}`;

        console.log('🔌 Connecting to WebSocket for job updates...');
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (isMounted) {
            console.log('✅ WebSocket connected for job updates');
            setConnected(true);
            reconnectAttemptsRef.current = 0; // Reset counter on successful connection

            // Start sending pings to keep connection alive
            pingIntervalRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, 30000); // Every 30 seconds
          }
        };

        ws.onmessage = (event) => {
          if (!isMounted) return;

          try {
            const message: WSMessage = JSON.parse(event.data);

            switch (message.type) {
              case 'connected':
                console.log('✅ WebSocket connected confirmation');
                break;

              case 'initial-state':
                if (message.jobs) {
                  const jobsMap = new Map<string, ExportJob>();
                  message.jobs.forEach(job => jobsMap.set(job.id, job));
                  setJobs(jobsMap);
                  console.log(`📋 Loaded ${message.jobs.length} jobs from initial state`);
                }
                break;

              case 'job-update':
                if (message.job) {
                  console.log(`📨 Received job update for ${message.job.id}: ${message.job.progress.phase || 'unknown phase'}`);
                  setJobs(prev => {
                    const next = new Map(prev);
                    next.set(message.job!.id, message.job!);
                    return next;
                  });
                }
                break;

              case 'pong':
                // Server responded to ping
                break;
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket connection error:', error);
        };

        ws.onclose = () => {
          console.log('🔌 WebSocket connection closed');

          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }

          if (isMounted) {
            setConnected(false);

            // Stop retrying after max attempts
            if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
              console.warn(`❌ WebSocket max reconnect attempts (${maxReconnectAttempts}) reached. Giving up.`);
              return;
            }

            reconnectAttemptsRef.current++;

            // Exponential backoff: 5s, 10s, 20s, 40s, 80s
            const delay = 5000 * Math.pow(2, reconnectAttemptsRef.current - 1);
            console.log(`🔄 Attempting to reconnect WebSocket in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);

            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMounted) {
                connect();
              }
            }, delay);
          }
        };

      } catch (error) {
        console.error('Error establishing WebSocket connection:', error);
        setConnected(false);
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        console.log('🔌 Closing WebSocket connection');
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []); // Empty deps - only run once on mount

  // Memoize these functions so they don't change on every render
  const getAllJobs = useCallback(() => Array.from(jobs.values()), [jobs]);

  const getRunningJobs = useCallback(() =>
    Array.from(jobs.values()).filter(job => job.status === 'running'),
    [jobs]
  );

  const getHistory = useCallback((limit?: number) => {
    const completedJobs = Array.from(jobs.values())
      .filter(job => job.status !== 'running')
      .sort((a, b) => (b.endTime || 0) - (a.endTime || 0));

    return limit ? completedJobs.slice(0, limit) : completedJobs;
  }, [jobs]);

  const getStats = useCallback(() => {
    const allJobs = Array.from(jobs.values());
    return {
      total: allJobs.length,
      running: allJobs.filter(j => j.status === 'running').length,
      completed: allJobs.filter(j => j.status === 'completed').length,
      failed: allJobs.filter(j => j.status === 'failed').length,
      cancelled: allJobs.filter(j => j.status === 'cancelled').length,
    };
  }, [jobs]);

  const getJob = useCallback((id: string) => jobs.get(id), [jobs]);

  // Manually sync jobs from API fetch (used when a new job is created)
  // This MERGES new jobs with existing WebSocket state instead of replacing
  const syncJobs = useCallback((newJobs: ExportJob[]) => {
    setJobs(prev => {
      const merged = new Map(prev);
      newJobs.forEach(job => merged.set(job.id, job));
      return merged;
    });
  }, []);

  return {
    connected,
    getAllJobs,
    getRunningJobs,
    getHistory,
    getStats,
    getJob,
    syncJobs,
  };
}
